'use client'

import { FederalDistrictCheckboxList, type FederalDistrictItem } from '@entities/federal-district'
import { Alert, AlertDescription, AlertTitle } from '@ui/base/alert'
import { Field, FieldLabel } from '@ui/base/field'

type DistrictPickerProps = {
  districts: FederalDistrictItem[] | undefined
  selectedCodes: string[]
  isLoading: boolean
  hasRequestError: boolean
  hasValidationError: boolean
  needsReselection: boolean
  onChange: (codes: string[]) => void
  onRetry: () => void
}

const labelId = 'request-federal-districts-label'
const optionsLabelId = 'request-federal-districts-options-label'
const requestErrorId = 'request-federal-districts-request-error'
const validationErrorId = 'request-wizard-error'

/** Федеральные округа, куда нужна отгрузка. Перечень приходит с сервера, выбрать нужно хотя бы один. */
export const DistrictPicker = ({
  districts,
  selectedCodes,
  isLoading,
  hasRequestError,
  hasValidationError,
  needsReselection,
  onChange,
  onRetry,
}: DistrictPickerProps) => {
  const toggleDistrict = (district: FederalDistrictItem, checked: boolean) =>
    onChange(
      checked
        ? [...selectedCodes, district.code]
        : selectedCodes.filter((selectedCode) => selectedCode !== district.code)
    )

  // Незаполненный выбор объясняет общая ошибка шага, отказ справочника — сообщение самого перечня.
  const describedBy = () => {
    if (hasValidationError) {
      return validationErrorId
    }

    return hasRequestError ? requestErrorId : undefined
  }
  const hasError = hasValidationError || hasRequestError

  return (
    <Field aria-labelledby={labelId} aria-describedby={describedBy()} aria-invalid={hasError} data-invalid={hasError}>
      <FieldLabel variant="section" id={labelId}>
        Федеральные округа
      </FieldLabel>
      {needsReselection && (
        <Alert variant="warning">
          <AlertTitle>Выберите федеральные округа заново</AlertTitle>
          <AlertDescription>В черновике сохранились округа, которых больше нет в справочнике.</AlertDescription>
        </Alert>
      )}
      <span id={optionsLabelId} className="sr-only">
        Варианты округов
      </span>
      <FederalDistrictCheckboxList
        districts={districts}
        selectedCodes={selectedCodes}
        onToggle={toggleDistrict}
        isLoading={isLoading}
        hasLoadError={hasRequestError}
        onRetry={onRetry}
        labelId={optionsLabelId}
        errorDescriptionId={requestErrorId}
      />
    </Field>
  )
}
